import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import ForceGraph2D from "react-force-graph-2d";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { formatIDR } from "../lib/format";

const NODE_COLORS = {
  Borrower: "#0055FF",
  GroupObligor: "#000000",
  Facility: "#1f1f1f",
  Collateral: "#008A00",
  Guarantor: "#F5A623",
  Covenant: "#777777",
  Shareholder: "#555555",
};

const NODE_LABELS = {
  Borrower: "Borrower",
  GroupObligor: "Group Obligor",
  Facility: "Facility",
  Collateral: "Collateral",
  Guarantor: "Guarantor",
  Covenant: "Covenant",
  Shareholder: "Shareholder",
};

const ALL_TYPES = Object.keys(NODE_COLORS);

export default function GraphView() {
  const [params] = useSearchParams();
  const initBorrower = params.get("borrower") || "";
  const [data, setData] = useState({ nodes: [], links: [] });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [borrowerFilter, setBorrowerFilter] = useState(initBorrower);
  const [typeFilter, setTypeFilter] = useState(new Set(ALL_TYPES));
  const [search, setSearch] = useState("");
  const fgRef = useRef();
  const containerRef = useRef();
  const [dim, setDim] = useState({ w: 800, h: 600 });

  useEffect(() => {
    const upd = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDim({ w: rect.width, h: Math.max(500, window.innerHeight - rect.top - 24) });
      }
    };
    upd();
    window.addEventListener("resize", upd);
    return () => window.removeEventListener("resize", upd);
  }, []);

  const fetchGraph = useCallback(async (borrower = "", types = null) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (borrower) qs.set("borrower_id", borrower);
      if (types && types.size < ALL_TYPES.length) qs.set("node_types", Array.from(types).join(","));
      const r = await api.get(`/graph/nodes?${qs.toString()}`);
      setData(r.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchGraph(borrowerFilter, typeFilter);
    // Initial load only — subsequent loads triggered by handleApply / Reset
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchGraph]);

  const handleApply = () => {
    fetchGraph(borrowerFilter, typeFilter);
  };

  const toggleType = (t) => {
    const ns = new Set(typeFilter);
    if (ns.has(t)) ns.delete(t); else ns.add(t);
    setTypeFilter(ns);
  };

  const filteredData = useMemo(() => {
    // Always rebuild from scratch with normalized string refs to prevent
    // react-force-graph from holding stale node-object refs across filter changes.
    const nodeIds = new Set(data.nodes.map((n) => n.id));

    let visibleNodes = data.nodes;
    let visibleNodeIds = nodeIds;

    if (search) {
      const s = search.toLowerCase();
      const matched = new Set(
        data.nodes
          .filter((n) => (n.label || "").toLowerCase().includes(s) || (n.raw_id || "").toLowerCase().includes(s))
          .map((n) => n.id)
      );
      const visible = new Set(matched);
      data.links.forEach((l) => {
        const sId = typeof l.source === "object" ? l.source.id : l.source;
        const tId = typeof l.target === "object" ? l.target.id : l.target;
        if (matched.has(sId) || matched.has(tId)) {
          if (nodeIds.has(sId)) visible.add(sId);
          if (nodeIds.has(tId)) visible.add(tId);
        }
      });
      visibleNodes = data.nodes.filter((n) => visible.has(n.id));
      visibleNodeIds = visible;
    }

    // Clone nodes (shallow) so force simulation can mutate x/y without
    // polluting our source-of-truth `data` state.
    const safeNodes = visibleNodes.map((n) => ({ ...n }));

    // Rebuild every link with string source/target and drop orphans.
    const safeLinks = data.links
      .map((l) => ({
        ...l,
        source: typeof l.source === "object" ? l.source.id : l.source,
        target: typeof l.target === "object" ? l.target.id : l.target,
      }))
      .filter((l) => visibleNodeIds.has(l.source) && visibleNodeIds.has(l.target));

    return { nodes: safeNodes, links: safeLinks };
  }, [search, data]);

  // Remount the force-graph canvas on every filter/data change so that
  // d3-force's internal cache of node-object refs is reset cleanly.
  const graphKey = useMemo(
    () => `${filteredData.nodes.length}-${filteredData.links.length}-${search}`,
    [filteredData, search]
  );

  const handleNodeClick = (node) => {
    setSelected(node);
    fgRef.current?.centerAt(node.x, node.y, 600);
    fgRef.current?.zoom(2.5, 600);
  };

  return (
    <div className="p-8 max-w-[1700px] mx-auto" data-testid="graph-page">
      <PageHeader
        title="Graph View"
        subtitle="Visualisasi jaringan obligor, fasilitas, collateral, guarantor, dan covenant"
      />

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        {/* Controls panel */}
        <aside className="space-y-4">
          <div className="border border-border rounded-md bg-card p-4">
            <h3 className="text-xs font-bold uppercase tracking-wider mb-3">Filter</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground">Borrower ID</label>
                <Input
                  placeholder="Kosong = semua"
                  value={borrowerFilter}
                  onChange={(e) => setBorrowerFilter(e.target.value)}
                  className="h-8 text-xs mt-1 mono"
                  data-testid="graph-borrower-input"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Cari Node</label>
                <Input
                  placeholder="Cari label/id…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-8 text-xs mt-1"
                  data-testid="graph-search-input"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-2">Tipe Node</label>
                <div className="space-y-1">
                  {ALL_TYPES.map((t) => (
                    <label key={t} className="flex items-center gap-2 text-xs cursor-pointer">
                      <input
                        type="checkbox"
                        checked={typeFilter.has(t)}
                        onChange={() => toggleType(t)}
                        data-testid={`graph-type-${t}`}
                      />
                      <span className="w-3 h-3 rounded-sm" style={{ background: NODE_COLORS[t] }} />
                      {NODE_LABELS[t]}
                    </label>
                  ))}
                </div>
              </div>
              <Button onClick={handleApply} className="w-full" size="sm" data-testid="graph-apply-filter">
                Terapkan Filter
              </Button>
              <Button variant="outline" onClick={() => { setBorrowerFilter(""); setTypeFilter(new Set(ALL_TYPES)); setSearch(""); fetchGraph("", new Set(ALL_TYPES)); }} className="w-full" size="sm">
                Reset
              </Button>
            </div>
          </div>

          <div className="border border-border rounded-md bg-card p-4">
            <h3 className="text-xs font-bold uppercase tracking-wider mb-2">Statistik</h3>
            <div className="text-xs space-y-1 mono">
              <div>Nodes: {filteredData.nodes.length}</div>
              <div>Links: {filteredData.links.length}</div>
              <div className="text-[#D0021B]">Alert nodes: {filteredData.nodes.filter((n) => n.has_alert).length}</div>
            </div>
          </div>

          {selected && (
            <div className="border border-border rounded-md bg-card p-4" data-testid="graph-node-detail">
              <h3 className="text-xs font-bold uppercase tracking-wider mb-2">Detail Node</h3>
              <div className="text-xs space-y-1">
                <div><span className="text-muted-foreground">Type:</span> <Badge variant="outline">{selected.node_type}</Badge></div>
                <div><span className="text-muted-foreground">ID:</span> <span className="mono">{selected.raw_id}</span></div>
                <div><span className="text-muted-foreground">Label:</span> {selected.label}</div>
                {selected.risk_rating && <div><span className="text-muted-foreground">Rating:</span> <span className="mono">{selected.risk_rating}</span></div>}
                {selected.approved_limit && <div><span className="text-muted-foreground">Approved:</span> {formatIDR(selected.approved_limit, { compact: true })}</div>}
                {selected.utilized_limit !== undefined && <div><span className="text-muted-foreground">Utilized:</span> {formatIDR(selected.utilized_limit, { compact: true })}</div>}
                {selected.lendable_value && <div><span className="text-muted-foreground">Lendable:</span> {formatIDR(selected.lendable_value, { compact: true })}</div>}
                {selected.has_alert && <Badge className="bg-[#D0021B] text-white">ALERT</Badge>}
                {selected.shared && <Badge variant="outline">SHARED</Badge>}
              </div>
            </div>
          )}
        </aside>

        {/* Graph canvas */}
        <div ref={containerRef} className="border border-border rounded-md bg-white overflow-hidden" data-testid="graph-canvas">
          {loading ? (
            <div className="flex items-center justify-center h-[500px] text-sm text-muted-foreground">Memuat graph…</div>
          ) : filteredData.nodes.length === 0 ? (
            <div className="flex items-center justify-center h-[500px] text-sm text-muted-foreground">Tidak ada data sesuai filter</div>
          ) : (
            <ForceGraph2D
              key={graphKey}
              ref={fgRef}
              graphData={filteredData}
              width={dim.w}
              height={dim.h}
              nodeLabel={(n) => `${n.node_type}: ${n.label}`}
              nodeColor={(n) => n.has_alert ? "#D0021B" : NODE_COLORS[n.node_type] || "#555"}
              nodeVal={(n) => n.node_type === "Borrower" ? 8 : n.node_type === "GroupObligor" ? 10 : 5}
              linkColor={(l) => l.color || (l.relationship === "MEMBER_OF" ? "#999" : "#CCC")}
              linkWidth={1}
              linkDirectionalArrowLength={3}
              linkDirectionalArrowRelPos={0.95}
              onNodeClick={handleNodeClick}
              cooldownTicks={80}
              nodeCanvasObjectMode={() => "after"}
              nodeCanvasObject={(node, ctx, globalScale) => {
                if (globalScale > 1.5) {
                  const label = node.label;
                  const fontSize = 10 / globalScale;
                  ctx.font = `${fontSize}px IBM Plex Sans, sans-serif`;
                  ctx.textAlign = "center";
                  ctx.fillStyle = "#222";
                  ctx.fillText(label, node.x, node.y + 8);
                }
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
